import React, { useState, useEffect, useContext } from "react";
import { Link, useNavigate } from "react-router-dom";
import { searchLds } from "../../services/fhpService";
import { useSearch } from "../../contexts/SearchContext";
import { ROUTES } from "../../Routes/Routes";
import { prefetchVacationPackagesDiscoveryHome } from "../../utils/prefetchVacationPackagesDiscoveryHome";
import { appendPublicSiteToken } from "../../utils/publicSiteUrl";
import dayjs from "dayjs";
import UnifiedHotelPackageCard from "../Common/UnifiedHotelPackageCard/UnifiedHotelPackageCard";
import { resolvePublicPackageCardDestination } from "../../utils/resolvePublicPackageCardDestination";
import { normalizeDestinationLabel } from "../../utils/resolveImageDestinationLabel";
import { normalizeLdsSearchDeals } from "../../utils/normalizeLdsSearchDeals";
import {
  getLdsHotelFromPackage,
  toLdsDdMmYyyy,
} from "../../utils/ldsDealHelpers";
import { buildPublicVacationPackageListCardDisplayMeta } from "../../utils/buildPublicVacationPackageListCardDisplayMeta";
import { useAppContext } from "../../contexts/AppContext";
import { PublicSiteContext, usePublicAgentHotelPricing } from "../../features/vacationPackagesPublic/PublicSiteContext";
import HotelPlaceholder from "../../assets/images/card/hotel.svg";
import "./RandomPackages.scss";

const POPULAR_DESTINATIONS = [
  "PAR",
  "ROM",
  "ATH",
  "BCN",
  "LON",
  "AMS",
  "PRG",
  "VIE",
  "BER",
  "MAD",
  "FCO",
  "LIS",
];

const BATCH_SIZE = 3;
const PER_DEST_TIMEOUT_MS = 28000;
const MAX_PACKAGES = 6;
const MAX_DESTINATIONS_TO_QUERY = 5;
const CACHE_KEY = "hr_home_random_packages_v1";
const CACHE_TTL_MS = 10 * 60 * 1000;

function withTimeout(promise, ms) {
  return new Promise((resolve, reject) => {
    const id = setTimeout(() => {
      const err = new Error("Timeout");
      err.code = "TIMEOUT";
      reject(err);
    }, ms);
    promise.then(
      (v) => {
        clearTimeout(id);
        resolve(v);
      },
      (e) => {
        clearTimeout(id);
        reject(e);
      }
    );
  });
}

function RandomPackagesSkeleton() {
  return (
    <div
      className="random-packages__skeleton-grid"
      aria-hidden="true"
      aria-busy="true"
    >
      {[0, 1, 2, 3].map((i) => (
        <div key={i} className="random-packages__skeleton-card">
          <div className="random-packages__skeleton-media" />
          <div className="random-packages__skeleton-body">
            <div className="random-packages__skeleton-line random-packages__skeleton-line--lg" />
            <div className="random-packages__skeleton-line random-packages__skeleton-line--md" />
            <div className="random-packages__skeleton-line random-packages__skeleton-line--sm" />
            <div className="random-packages__skeleton-footer">
              <div className="random-packages__skeleton-price" />
              <div className="random-packages__skeleton-btn" />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

const RandomPackages = () => {
  const navigate = useNavigate();

  const searchContext = useSearch();
  const calculateLDSDealFareForRoomsComposition =
    searchContext?.calculateLDSDealFareForRoomsComposition;
  const calendarDateArrival = searchContext?.calendarDateArrival;
  const calendarDateLeave = searchContext?.calendarDateLeave;
  const roomInfo = searchContext?.roomInfo;
  const agentHotelPricingForPublic = usePublicAgentHotelPricing();
  const { USDrate } = useAppContext();
  const publicSiteCtx = useContext(PublicSiteContext);
  const publicSitePricingFromCtx = publicSiteCtx?.publicSitePricingSettings ?? null;

  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    let idleTimerId = null;
    let idleCallbackId = null;

    const readCachedPackages = () => {
      try {
        const raw = sessionStorage.getItem(CACHE_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        if (!parsed || typeof parsed !== "object") return null;
        if (!Array.isArray(parsed.packages)) return null;
        const ageMs = Date.now() - Number(parsed.ts || 0);
        if (ageMs > CACHE_TTL_MS) return null;
        return parsed.packages;
      } catch {
        return null;
      }
    };

    const writeCachedPackages = (nextPackages) => {
      try {
        sessionStorage.setItem(
          CACHE_KEY,
          JSON.stringify({
            ts: Date.now(),
            packages: Array.isArray(nextPackages) ? nextPackages : [],
          })
        );
      } catch {
        // ignore
      }
    };

    const run = async () => {
      setLoading(true);
      setFetchError(null);

      let depDate;
      let retDate;
      if (calendarDateArrival && calendarDateLeave) {
        depDate = toLdsDdMmYyyy(calendarDateArrival) || calendarDateArrival;
        retDate = toLdsDdMmYyyy(calendarDateLeave) || calendarDateLeave;
      } else {
        const nextMonth = dayjs().add(1, "month");
        depDate = nextMonth.startOf("month").format("DD/MM/YYYY");
        retDate = nextMonth.startOf("month").add(7, "days").format("DD/MM/YYYY");
      }

      let roomCompositions;
      if (roomInfo?.rooms && roomInfo.rooms.length > 0) {
        roomCompositions = roomInfo.rooms.map((room) => {
          const adults = room.adults ?? room.AdultsCount ?? 2;
          const kids = room.kids ?? room.KidsCount ?? 0;
          const infants = room.infants ?? room.InfantsCount ?? 0;
          let kidsAges = room.kidsAges ?? room.KidsAges ?? [];
          if (!Array.isArray(kidsAges)) kidsAges = [];
          while (kidsAges.length < kids) kidsAges.push(2);
          if (kidsAges.length > kids) kidsAges = kidsAges.slice(0, kids);
          return { adults, kids, infants, kidsAges };
        });
      } else {
        roomCompositions = [{ adults: 2, kids: 0, infants: 0, kidsAges: [] }];
      }

      const shuffled = [...POPULAR_DESTINATIONS]
        .sort(() => Math.random() - 0.5)
        .slice(0, MAX_DESTINATIONS_TO_QUERY);

      const fetchForDestination = async (destCode) => {
        const searchData = {
          dep_date: depDate,
          ret_date: retDate,
          city: destCode,
          categories: [],
          room_compositions: roomCompositions,
        };

        let response;
        try {
          response = await withTimeout(searchLds(searchData), PER_DEST_TIMEOUT_MS);
        } catch (e) {
          if (e?.code === "TIMEOUT") {
            console.warn(`[RandomPackages] Timeout for ${destCode}`);
          } else {
            console.warn(`[RandomPackages] Request failed for ${destCode}:`, e);
          }
          return [];
        }

        if (!response || (response.status && response.status >= 400)) {
          return [];
        }

        const raw = response.data ?? response;
        const { deals, opError } = normalizeLdsSearchDeals(raw);
        if (opError) {
          return [];
        }

        return deals
          .filter((pkg) => Boolean(getLdsHotelFromPackage(pkg)))
          .slice(0, 1)
          .map((pkg) => ({
            ...pkg,
            destinationCode: destCode,
          }));
      };

      try {
        const allPackages = [];

        for (let i = 0; i < shuffled.length && allPackages.length < MAX_PACKAGES; i += BATCH_SIZE) {
          if (cancelled) break;
          const chunk = shuffled.slice(i, i + BATCH_SIZE);
          const batchLists = await Promise.all(chunk.map((code) => fetchForDestination(code)));
          if (cancelled) break;
          for (const list of batchLists) {
            allPackages.push(...list);
          }
        }

        if (cancelled) return;

        const picked = [...allPackages]
          .sort(() => Math.random() - 0.5)
          .slice(0, MAX_PACKAGES);

        setPackages(picked);
        writeCachedPackages(picked);
      } catch (error) {
        console.error("RandomPackages: Error fetching packages:", error);
        if (!cancelled) {
          setPackages([]);
          setFetchError(
            "לא ניתן לטעון את החבילות כרגע. נסו לרענן את העמוד או לחפש ידנית."
          );
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    const cached = readCachedPackages();
    if (cached && cached.length > 0) {
      setPackages(cached.slice(0, MAX_PACKAGES));
      setLoading(false);
      return () => {
        cancelled = true;
      };
    }

    const scheduleRun = () => {
      if (typeof window !== "undefined" && typeof window.requestIdleCallback === "function") {
        idleCallbackId = window.requestIdleCallback(
          () => {
            run();
          },
          { timeout: 800 }
        );
      } else {
        idleTimerId = window.setTimeout(() => {
          run();
        }, 250);
      }
    };

    scheduleRun();

    return () => {
      cancelled = true;
      if (idleCallbackId != null && typeof window !== "undefined" && window.cancelIdleCallback) {
        window.cancelIdleCallback(idleCallbackId);
      }
      if (idleTimerId != null) {
        window.clearTimeout(idleTimerId);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- סקשן בית: טעינה חד-פעמית; ערכי הקונטקסט משמשים רק לצילום ראשוני
  }, []);

  const handlePackageClick = (pkg) => {
    navigate(appendPublicSiteToken(`/${ROUTES.VACATION_PACKAGES}`), {
      state: { selectedPackage: pkg },
    });
  };

  const getHotelImage = (pkg) => {
    const hotel = getLdsHotelFromPackage(pkg);
    return hotel?.resourceUrl || HotelPlaceholder;
  };

  const getHotelName = (pkg) => {
    const hotel = getLdsHotelFromPackage(pkg);
    return hotel?.hotelName || "מלון";
  };

  const getDestinationName = (destCode) => {
    return (
      resolvePublicPackageCardDestination({ destinationCode: destCode }).label ||
      destCode
    );
  };

  const vacationPackagesPath = appendPublicSiteToken(`/${ROUTES.VACATION_PACKAGES}`);
  if (!loading && !fetchError && packages.length === 0) return null;

  return (
    <div className="random-packages" aria-labelledby="home-featured-packages-title">
      <header className="random-packages__head">
        <div className="random-packages__head-row">
          <h2 className="random-packages__title" id="home-featured-packages-title">
            חבילות נופש מומלצות
          </h2>
          <Link to={vacationPackagesPath} className="random-packages__view-all">
            צפה בכל
          </Link>
        </div>
        <p className="random-packages__subtitle">
          מבחר עדכני מיעדים פופולריים — לחצו על כרטיס לפרטים או עברו לחיפוש מלא.
        </p>
      </header>

      <div className="random-packages__content">
        {loading && <RandomPackagesSkeleton />}

        {!loading && fetchError && (
          <div className="random-packages__state random-packages__state--error" role="alert">
            <p className="random-packages__state-text">{fetchError}</p>
            <div className="random-packages__state-actions">
              <button
                type="button"
                className="random-packages__ghost-btn"
                onClick={() => window.location.reload()}
              >
                רענון עמוד
              </button>
              <Link
                to={vacationPackagesPath}
                className="random-packages__primary-btn"
                onMouseEnter={prefetchVacationPackagesDiscoveryHome}
                onFocus={prefetchVacationPackagesDiscoveryHome}
              >
                חיפוש חבילות
              </Link>
            </div>
          </div>
        )}

        {!loading && !fetchError && packages.length > 0 && (
          <div className="random-packages-grid">
            {packages.map((pkg, index) => {
              const hotel = getLdsHotelFromPackage(pkg);
              const listMeta = buildPublicVacationPackageListCardDisplayMeta({
                dealRow: pkg,
                calculateLDSDealFareForRoomsComposition,
                publicSitePricingSettings: publicSitePricingFromCtx,
                agentHotelPricing: agentHotelPricingForPublic,
                usdToIlsRate: USDrate,
                pricingSourceTag: "RandomPackages.card",
                surface: "main_home_random",
                roomInfo,
              });
              const destLabel = normalizeDestinationLabel(
                getDestinationName(pkg.destinationCode)
              );
              const stars =
                hotel?.stars ||
                hotel?.starRating ||
                (hotel?.category
                  ? parseInt(String(hotel.category).replace(/\D/g, ""), 10)
                  : 0) ||
                0;
              return (
                <UnifiedHotelPackageCard
                  key={`${pkg.destinationCode}-${hotel?.hotelId ?? index}-${index}`}
                  hotelName={getHotelName(pkg)}
                  imageUrl={getHotelImage(pkg)}
                  imageAlt={getHotelName(pkg)}
                  isFallbackImage={!hotel?.resourceUrl}
                  destinationBadgeLabel={destLabel}
                  destinationCode={pkg.destinationCode}
                  stars={stars}
                  listDisplayMeta={{
                    dateRangeLabel: listMeta.dateRangeLabel,
                    nightsLabel: listMeta.nightsLabel,
                    currencyDisplay: listMeta.currencyDisplay,
                  }}
                  onMediaClick={() => handlePackageClick(pkg)}
                  onCtaClick={() => handlePackageClick(pkg)}
                  metaDestination={getDestinationName(pkg.destinationCode)}
                />
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default RandomPackages;
